import type { ReactNode } from 'react';
import { Button } from '@paige/ui';
import type { DayName, OffWindow } from './desktop-types';
import { NEW_WINDOW } from './policy-draft';
import { DAY_SHORT, WEEK, windowWords } from './policy-words';

/**
 * What a screen reader calls an off time: "Off time 1", or "RTX 4090 off time 1" while each card has its own list, so
 * every control says which card and which off time it sets.
 */
function whereOf(scope: string | undefined, n: number): string {
  return scope ? `${scope} off time ${n}` : `Off time ${n}`;
}

/**
 * An off time's days, one toggle a day, Monday first; each named "Off time 1: off on Mon", the day as the button shows
 * it, so a person who speaks what they see can press it (WCAG 2.5.3), and pressed when off.
 */
function Days({
  w,
  where,
  disabled,
  onChange,
}: {
  w: OffWindow;
  where: string;
  disabled: boolean;
  onChange: (w: OffWindow) => void;
}): ReactNode {
  const toggle = (d: DayName) =>
    onChange({ ...w, days: w.days.includes(d) ? w.days.filter((x) => x !== d) : [...w.days, d] });
  return (
    <span className="dg-off__days" role="group" aria-label={`${where} days`}>
      {WEEK.map((d) => (
        <button
          key={d}
          type="button"
          className="dg-day"
          aria-label={`${where}: off on ${DAY_SHORT[d]}`}
          aria-pressed={w.days.includes(d)}
          disabled={disabled}
          onClick={() => toggle(d)}
        >
          {DAY_SHORT[d]}
        </button>
      ))}
    </span>
  );
}

/** A start or end time, 24-hour HH:mm as the browser's time field gives it; `name` is what a screen reader says. */
function TimeField({
  label,
  name,
  value,
  disabled,
  onValue,
}: {
  label: string;
  name: string;
  value: string;
  disabled: boolean;
  onValue: (v: string) => void;
}): ReactNode {
  return (
    <label className="dg-off__time">
      {label}
      <input
        type="time"
        aria-label={name}
        value={value}
        disabled={disabled}
        onChange={(e) => onValue(e.target.value)}
      />
    </label>
  );
}

const TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

/**
 * What an off time's start and end mean when it is not plain: an end before the start runs overnight, and an end equal
 * to the start is the whole day (PolicyClock). Said only once both times are set.
 */
function SpanHint({ w }: { w: OffWindow }): ReactNode {
  if (!TIME.test(w.from) || !TIME.test(w.to) || w.to > w.from) return null;
  return (
    <span className="dg-quiet">
      {w.to === w.from
        ? 'The whole day: same start and end'
        : 'Runs past midnight into the next day'}
    </span>
  );
}

interface RowProps {
  w: OffWindow;
  /** Its place in the list, from 1. */
  n: number;
  /** The card the list is for, while each card has its own; none for the one shared list. */
  scope: string | undefined;
  disabled: boolean;
  onChange: (w: OffWindow) => void;
  onRemove: () => void;
}

/** One off time: its days as toggles, from and to, and Remove. */
function OffTimeRow({ w, n, scope, ...act }: RowProps): ReactNode {
  const { disabled, onChange } = act;
  const where = whereOf(scope, n);
  return (
    <li className="dg-off" aria-label={`${where}: ${windowWords(w)}`}>
      <Days w={w} where={where} disabled={disabled} onChange={onChange} />
      <TimeField
        label="From"
        name={`${where} from`}
        value={w.from}
        disabled={disabled}
        onValue={(from) => onChange({ ...w, from })}
      />
      <TimeField
        label="To"
        name={`${where} to`}
        value={w.to}
        disabled={disabled}
        onValue={(to) => onChange({ ...w, to })}
      />
      <Button
        type="button"
        size="sm"
        variant="secondary"
        aria-label={`Remove ${scope ? where : `off time ${n}`}`}
        disabled={disabled}
        onClick={act.onRemove}
      >
        Remove
      </Button>
      <SpanHint w={w} />
    </li>
  );
}

/**
 * The times a card is not used (in the machine's own zone): each with its days, from and to; add and remove. A window
 * whose end is before its start runs overnight, into the next morning.
 */
export function OffTimes({
  off,
  scope,
  disabled,
  onChange,
}: {
  off: OffWindow[];
  /** The card's name while each card has its own list, so each control names its card. */
  scope?: string | undefined;
  disabled: boolean;
  onChange: (off: OffWindow[]) => void;
}): ReactNode {
  return (
    <div className="dg-offs">
      <ul className="dg-offs__list">
        {off.map((w, i) => (
          <OffTimeRow
            key={i}
            w={w}
            n={i + 1}
            scope={scope}
            disabled={disabled}
            onChange={(next) => onChange(off.map((x, j) => (j === i ? next : x)))}
            onRemove={() => onChange(off.filter((_, j) => j !== i))}
          />
        ))}
      </ul>
      {off.length === 0 ? <p className="dg-quiet">No off times: used at any time.</p> : null}
      <Button
        type="button"
        size="sm"
        variant="secondary"
        aria-label={scope ? `Add an off time for ${scope}` : undefined}
        disabled={disabled || off.length >= 28}
        onClick={() => onChange([...off, NEW_WINDOW])}
      >
        Add an off time
      </Button>
    </div>
  );
}
